import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Bot,
  Sparkles,
  Copy,
  FileText,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  BarChart3,
  PieChart,
  MapPin,
  DollarSign,
  Home,
  Building,
  Users,
  Calendar,
  Target,
  Zap,
  ChevronRight,
  BrainCircuit,
  Info,
  PlusCircle,
  Trash2,
  History,
  X,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { cn } from "../utils/helpers";
import { localStorageUtils, STORAGE_KEYS } from "../utils/localStorage";
import { useStore } from "../store/useStore";
import * as model2Service from "../services/model2Service";

type ChatRole = "user" | "assistant";

interface UiMessage {
  id: string;
  role: ChatRole;
  content: string;
  timestamp: string | null;
}

interface SessionMeta {
  preview: string;
  messageCount: number;
  updated: string;
}

const DEFAULT_ASSISTANT_MESSAGE = "Hey, let's get started.";
const SOURCE_TAG_REGEX = /\[source:([^\]]+)\]/gi;

const getFilenameFromPath = (value: string) => {
  const withoutQuery = value.split("?")[0];
  const segments = withoutQuery.split("/").filter(Boolean);
  const filename = segments[segments.length - 1] || value;
  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
};

const getSourceDisplayName = (source: model2Service.Source) => {
  if (source.title?.trim()) return source.title.trim();
  const assetPath = source.asset?.file_path || source.asset?.url;
  if (assetPath?.trim()) return getFilenameFromPath(assetPath);
  return source.id;
};

const normalizeSourceId = (sourceId: string) =>
  sourceId.trim().replace(/^source:/i, "");

const buildSourceTitleMap = (sources: model2Service.Source[]) => {
  const nextMap: Record<string, string> = {};

  sources.forEach((source) => {
    const displayTitle = getSourceDisplayName(source);
    const rawId = source.id?.trim();
    if (!rawId) return;

    const normalizedId = normalizeSourceId(rawId);
    nextMap[rawId] = displayTitle;
    nextMap[normalizedId] = displayTitle;
    nextMap[`source:${normalizedId}`] = displayTitle;
  });

  return nextMap;
};

const resolveSourceTitle = (
  sourceId: string,
  sourceTitleById: Record<string, string>,
) => {
  const rawId = sourceId.trim();
  const normalizedId = normalizeSourceId(rawId);
  const resolved =
    sourceTitleById[rawId] ||
    sourceTitleById[normalizedId] ||
    sourceTitleById[`source:${normalizedId}`];

  if (!resolved) return null;

  return resolved
    .replace(/\.(xlsx|xls|csv|json|pdf|docx|doc|txt|pptx|ppt)$/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
};

const parseMessageSources = (content: string) => {
  const sourceIds: string[] = [];
  const cleaned = content.replace(SOURCE_TAG_REGEX, (_tag, rawId: string) => {
    const sourceId = rawId.trim();
    if (sourceId && !sourceIds.includes(sourceId)) {
      sourceIds.push(sourceId);
    }
    return "";
  });

  return {
    text: cleaned.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim(),
    sourceIds,
  };
};

const Typewriter = ({
  content,
  animate,
  onComplete,
  sourceTitleById,
  speed = 15,
}: {
  content: string;
  animate: boolean;
  onComplete?: () => void;
  sourceTitleById: Record<string, string>;
  speed?: number;
}) => {
  const [visibleLength, setVisibleLength] = useState(
    animate ? 0 : content.length,
  );
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!animate) {
      setVisibleLength(content.length);
      return;
    }

    setVisibleLength(0);
    let completed = false;

    const timer = setInterval(() => {
      setVisibleLength((prev) => {
        const next = prev + 1;
        if (next >= content.length) {
          clearInterval(timer);
          if (!completed) {
            completed = true;
            onCompleteRef.current?.();
          }
          return content.length;
        }
        return next;
      });
    }, speed);

    return () => clearInterval(timer);
  }, [animate, content, speed]);

  return (
    <>
      {formatMessage(content.slice(0, visibleLength), sourceTitleById)}
    </>
  );
};

const formatMessage = (
  content: string,
  sourceTitleById: Record<string, string>,
) => {
  if (!content) return null;

  const { text, sourceIds } = parseMessageSources(content);
  const lines = text ? text.split("\n") : [];
  const resolvedSources = sourceIds
    .map((sourceId) => ({
      sourceId,
      title: resolveSourceTitle(sourceId, sourceTitleById),
    }))
    .filter(
      (item): item is { sourceId: string; title: string } =>
        Boolean(item.title),
    );

  const sourceChips = resolvedSources.map((item) => (
    <span
      key={`source-chip-${item.sourceId}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-300/80 dark:border-slate-600/80 bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 text-[11px] font-semibold text-slate-700 dark:text-slate-100 shadow-sm"
    >
      <FileText size={11} className="text-slate-500 dark:text-slate-300" />
      <span>{item.title}</span>
    </span>
  ));
  const lastNonEmptyLineIndex = lines.reduce(
    (lastIndex, line, index) => (line.trim() ? index : lastIndex),
    -1,
  );
  const iconMap: { [key: string]: any } = {
    price: DollarSign,
    market: TrendingUp,
    data: BarChart3,
    analysis: PieChart,
    location: MapPin,
    property: Home,
    building: Building,
    tenant: Users,
    date: Calendar,
    target: Target,
    growth: Zap,
    success: CheckCircle,
    warning: AlertCircle,
  };

  return (
    <>
      {lines.map((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine && index !== lines.length - 1)
          return <div key={index} className="h-2" />;

        let IconComponent = ChevronRight;
        let iconColor = "text-slate-400";
        let bgColor = "bg-transparent";

        for (const [keyword, Icon] of Object.entries(iconMap)) {
          if (trimmedLine.toLowerCase().includes(keyword)) {
            IconComponent = Icon;
            iconColor = "text-indigo-500";
            bgColor = "bg-indigo-50 dark:bg-indigo-500/10";
            break;
          }
        }

        if (/^\d+\./.test(trimmedLine)) {
          IconComponent = CheckCircle;
          iconColor = "text-emerald-500";
          bgColor = "bg-emerald-50 dark:bg-emerald-500/10";
        }

        const isSpecial = trimmedLine.includes("**") || /^\d+\./.test(trimmedLine);
        const cleanLine = trimmedLine.replace(/\*\*/g, "");

        return (
          <div
            key={index}
            className={cn(
              "flex items-start gap-3 mb-2 animate-in fade-in duration-300",
              isSpecial ? "ml-1" : "",
            )}
          >
            {isSpecial && (
              <div className={cn("p-1 rounded-md mt-0.5 shrink-0", bgColor)}>
                <IconComponent size={14} className={iconColor} />
              </div>
            )}
            <span
              className={cn(
                "leading-relaxed whitespace-pre-wrap",
                isSpecial
                  ? "text-slate-800 dark:text-slate-200 font-medium"
                  : "text-slate-600 dark:text-slate-300",
              )}
            >
              {cleanLine}
              {index === lastNonEmptyLineIndex && resolvedSources.length > 0 && (
                <span className="inline-flex flex-wrap items-center gap-1 align-middle ml-2">
                  {sourceChips}
                </span>
              )}
            </span>
          </div>
        );
      })}
      {lines.length === 0 && resolvedSources.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {sourceChips}
        </div>
      )}
    </>
  );
};

export default function Chat() {
  const addNotification = useStore((state) => state.addNotification);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionActionLoading, setIsSessionActionLoading] = useState(false);
  const [isInitializingChat, setIsInitializingChat] = useState(false);
  const [model2NotebookId, setModel2NotebookId] = useState<string | null>(null);
  const [model2SessionId, setModel2SessionId] = useState<string | null>(null);
  const [model2SourceIds, setModel2SourceIds] = useState<string[]>([]);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<model2Service.ChatSession[]>([]);
  const [sessionMeta, setSessionMeta] = useState<Record<string, SessionMeta>>(
    {},
  );
  const [sourceTitleById, setSourceTitleById] = useState<
    Record<string, string>
  >({});
  const [isHistoryOpenMobile, setIsHistoryOpenMobile] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(
    null,
  );
  const [pendingDeleteSessionId, setPendingDeleteSessionId] = useState<
    string | null
  >(null);

  const messagesRef = useRef<UiMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
    });
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const sortSessions = useCallback((items: model2Service.ChatSession[]) => {
    return [...items].sort((a, b) => {
      const aTime = new Date(a.updated || 0).getTime();
      const bTime = new Date(b.updated || 0).getTime();
      return bTime - aTime;
    });
  }, []);

  const truncateText = useCallback((value: string, max = 42) => {
    if (value.length <= max) return value;
    return `${value.slice(0, max).trimEnd()}...`;
  }, []);

  const getPreviewFromApiMessages = useCallback(
    (messages: model2Service.ChatMessage[]) => {
      const firstHuman = messages.find(
        (message) => message.type === "human" && message.content?.trim(),
      );
      return firstHuman
        ? truncateText(firstHuman.content.trim())
        : DEFAULT_ASSISTANT_MESSAGE;
    },
    [truncateText],
  );

  const refreshSourceTitles = useCallback(async () => {
    try {
      const sources = await model2Service.getSources(100, 0);
      const nextMap = buildSourceTitleMap(sources);
      setSourceTitleById((prev) => ({ ...prev, ...nextMap }));
    } catch (error) {
      console.warn("Failed to refresh source titles:", error);
    }
  }, []);

  const refreshSessions = useCallback(
    async (notebookId: string) => {
      const allSessions = await model2Service.getChatSessions(notebookId);
      const sorted = sortSessions(allSessions);

      const detailResults = await Promise.allSettled(
        sorted.map((session) => model2Service.getChatSession(session.id)),
      );

      const nextMeta: Record<string, SessionMeta> = {};
      sorted.forEach((session, index) => {
        const details = detailResults[index];
        if (details.status === "fulfilled") {
          const chatMessages = (details.value.messages || []).filter(
            (message) => message.type === "human" || message.type === "ai",
          );
          nextMeta[session.id] = {
            preview: getPreviewFromApiMessages(chatMessages),
            messageCount: chatMessages.length,
            updated: details.value.updated || session.updated,
          };
        } else {
          nextMeta[session.id] = {
            preview: DEFAULT_ASSISTANT_MESSAGE,
            messageCount: session.message_count || 0,
            updated: session.updated,
          };
        }
      });

      setSessionMeta(nextMeta);
      setSessions(sorted);
      return sorted;
    },
    [getPreviewFromApiMessages, sortSessions],
  );

  const mapApiMessages = useCallback(
    (apiMessages: model2Service.ChatMessage[]): UiMessage[] =>
      apiMessages
        .filter((msg) => msg.type === "human" || msg.type === "ai")
        .map((msg) => ({
          id: msg.id,
          role: msg.type === "human" ? "user" : "assistant",
          content: msg.content || "",
          timestamp: msg.timestamp,
        })),
    [],
  );

  const loadSessionMessages = useCallback(
    async (sessionId: string) => {
      const session = await model2Service.getChatSession(sessionId);
      const mapped = mapApiMessages(session.messages || []);
      const displayMessages =
        mapped.length > 0
          ? mapped
          : [
              {
                id: `default-${sessionId}`,
                role: "assistant" as const,
                content: DEFAULT_ASSISTANT_MESSAGE,
                timestamp: null,
              },
            ];
      setMessages(displayMessages);
      setTypingMessageId(null);
      return mapped;
    },
    [mapApiMessages],
  );

  const initializeModel2Chat = useCallback(async () => {
    try {
      setIsInitializingChat(true);

      let notebookId = localStorageUtils.get<string | null>(
        STORAGE_KEYS.MODEL2_NOTEBOOK_ID,
        null,
      );

      if (!notebookId) {
        const initResult = await model2Service.initializeModel2();
        notebookId = initResult.notebookId;
        localStorageUtils.set(
          STORAGE_KEYS.MODEL2_NOTEBOOK_ID,
          initResult.notebookId,
        );
        localStorageUtils.set(
          STORAGE_KEYS.MODEL2_CHAT_MODEL_ID,
          initResult.chatModelId,
        );
        localStorageUtils.set(
          STORAGE_KEYS.MODEL2_EMBEDDING_MODEL_ID,
          initResult.embeddingModelId,
        );
      }

      if (!notebookId) return;
      setModel2NotebookId(notebookId);

      const sourceIds = localStorageUtils.get<string[]>(
        STORAGE_KEYS.MODEL2_SOURCE_IDS,
        [],
      );
      setModel2SourceIds(sourceIds);
      await refreshSourceTitles();

      const allSessions = await refreshSessions(notebookId);
      const persistedSessionId = localStorageUtils.get<string | null>(
        STORAGE_KEYS.MODEL2_ACTIVE_CHAT_SESSION_ID,
        null,
      );

      let activeSession =
        allSessions.find((session) => session.id === persistedSessionId) ||
        allSessions[0];

      if (!activeSession) {
        activeSession = await model2Service.createChatSession(
          notebookId,
          "Dashboard Chat",
        );
        const nextSessions = await refreshSessions(notebookId);
        const fromList = nextSessions.find((session) => session.id === activeSession.id);
        if (fromList) activeSession = fromList;
      }

      setModel2SessionId(activeSession.id);
      localStorageUtils.set(
        STORAGE_KEYS.MODEL2_ACTIVE_CHAT_SESSION_ID,
        activeSession.id,
      );
      await loadSessionMessages(activeSession.id);
    } catch (error) {
      console.error("Failed to initialize Model 2 chat:", error);
      addNotification("error", "Failed to load chat session");
    } finally {
      setIsInitializingChat(false);
    }
  }, [addNotification, loadSessionMessages, refreshSessions, refreshSourceTitles]);

  useEffect(() => {
    initializeModel2Chat();
  }, [initializeModel2Chat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, typingMessageId, scrollToBottom]);

  const getContextConfig = useCallback(async () => {
    const sourceIds = localStorageUtils.get<string[]>(
      STORAGE_KEYS.MODEL2_SOURCE_IDS,
      model2SourceIds,
    );
    setModel2SourceIds(sourceIds);

    if (sourceIds.length === 0) return {};

    const sources = await model2Service.getSources(100, 0);
    const nextMap = buildSourceTitleMap(sources);
    setSourceTitleById((prev) => ({ ...prev, ...nextMap }));

    const readySources = sources.filter(
      (source) =>
        sourceIds.includes(source.id) &&
        source.status === "completed" &&
        source.embedded === true,
    );

    const contextConfig: { [key: string]: string } = {};
    readySources.forEach((source) => {
      contextConfig[source.id] = "full content";
    });

    return contextConfig;
  }, [model2SourceIds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !model2SessionId || !model2NotebookId)
      return;

    const query = input.trim();
    const optimisticUserId = `local-user-${Date.now()}`;
    const previousMessageIds = new Set(messagesRef.current.map((m) => m.id));

    setMessages((prev) => [
      ...prev,
      {
        id: optimisticUserId,
        role: "user",
        content: query,
        timestamp: new Date().toISOString(),
      },
    ]);
    scrollToBottom("auto");
    setInput("");
    setIsLoading(true);

    try {
      const contextConfig = await getContextConfig();
      const contextResponse = await model2Service.getChatContext(
        model2NotebookId,
        contextConfig,
        {},
      );

      const executeResponse = await model2Service.executeChatQuery({
        session_id: model2SessionId,
        message: query,
        context: contextResponse.context,
      });

      const nextMessages = mapApiMessages(executeResponse.messages);
      const newestAssistantMessage = [...nextMessages]
        .reverse()
        .find(
          (message) =>
            message.role === "assistant" && !previousMessageIds.has(message.id),
        );

      setMessages(nextMessages);
      setTypingMessageId(newestAssistantMessage?.id || null);

      if (model2SessionId) {
        const firstHumanMessage = nextMessages.find(
          (message) => message.role === "user" && message.content.trim(),
        );
        const nowIso = new Date().toISOString();
        setSessionMeta((prev) => ({
          ...prev,
          [model2SessionId]: {
            preview: firstHumanMessage
              ? truncateText(firstHumanMessage.content.trim())
              : DEFAULT_ASSISTANT_MESSAGE,
            messageCount: nextMessages.length,
            updated: nowIso,
          },
        }));
        setSessions((prev) =>
          sortSessions(
            prev.map((session) =>
              session.id === model2SessionId
                ? { ...session, updated: nowIso, message_count: nextMessages.length }
                : session,
            ),
          ),
        );
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      addNotification("error", "Unable to retrieve response from Model 2");
      setMessages((prev) =>
        prev.filter((message) => message.id !== optimisticUserId),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = async () => {
    if (!model2NotebookId || isSessionActionLoading || isLoading) return;

    try {
      setIsSessionActionLoading(true);
      const session = await model2Service.createChatSession(
        model2NotebookId,
        `Dashboard Chat ${new Date().toISOString()}`,
      );

      setModel2SessionId(session.id);
      localStorageUtils.set(STORAGE_KEYS.MODEL2_ACTIVE_CHAT_SESSION_ID, session.id);
      await loadSessionMessages(session.id);
      await refreshSessions(model2NotebookId);
      setTypingMessageId(null);
      setInput("");
      setIsHistoryOpenMobile(false);
      addNotification("success", "Started a new chat session");
    } catch (error) {
      console.error("Failed to create new chat:", error);
      addNotification("error", "Unable to start a new chat session");
    } finally {
      setIsSessionActionLoading(false);
    }
  };

  const handleSwitchSession = async (sessionId: string) => {
    if (
      sessionId === model2SessionId ||
      isSessionActionLoading ||
      isLoading ||
      !model2NotebookId
    ) {
      setIsHistoryOpenMobile(false);
      return;
    }

    try {
      setIsSessionActionLoading(true);
      setModel2SessionId(sessionId);
      localStorageUtils.set(STORAGE_KEYS.MODEL2_ACTIVE_CHAT_SESSION_ID, sessionId);
      await loadSessionMessages(sessionId);
      await refreshSessions(model2NotebookId);
      setTypingMessageId(null);
      setInput("");
      setIsHistoryOpenMobile(false);
      scrollToBottom("auto");
    } catch (error) {
      console.error("Failed to switch session:", error);
      addNotification("error", "Unable to switch to selected chat session");
    } finally {
      setIsSessionActionLoading(false);
    }
  };

  const openDeleteSessionModal = (sessionId: string) => {
    if (!model2NotebookId || isSessionActionLoading || isLoading) return;
    setPendingDeleteSessionId(sessionId);
  };

  const closeDeleteSessionModal = () => {
    if (isSessionActionLoading) return;
    setPendingDeleteSessionId(null);
  };

  const confirmDeleteSession = async () => {
    if (
      !model2NotebookId ||
      !pendingDeleteSessionId ||
      isSessionActionLoading ||
      isLoading
    ) {
      return;
    }

    const sessionId = pendingDeleteSessionId;
    try {
      setIsSessionActionLoading(true);
      setDeletingSessionId(sessionId);
      await model2Service.deleteChatSession(sessionId);

      let remainingSessions = await refreshSessions(model2NotebookId);
      let nextSession = remainingSessions[0];

      if (!nextSession) {
        const created = await model2Service.createChatSession(
          model2NotebookId,
          "Dashboard Chat",
        );
        remainingSessions = await refreshSessions(model2NotebookId);
        nextSession =
          remainingSessions.find((session) => session.id === created.id) ||
          remainingSessions[0] ||
          created;
      }

      setModel2SessionId(nextSession.id);
      localStorageUtils.set(
        STORAGE_KEYS.MODEL2_ACTIVE_CHAT_SESSION_ID,
        nextSession.id,
      );
      await loadSessionMessages(nextSession.id);
      setTypingMessageId(null);
      setInput("");
      setIsHistoryOpenMobile(false);
      scrollToBottom("auto");
      addNotification("success", "Chat session deleted");
    } catch (error) {
      console.error("Failed to delete chat session:", error);
      addNotification("error", "Unable to delete this chat session");
    } finally {
      setPendingDeleteSessionId(null);
      setDeletingSessionId(null);
      setIsSessionActionLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textArea);
        if (!copied) {
          throw new Error("Fallback copy failed");
        }
      }
      addNotification("success", "Response copied");
    } catch (error) {
      console.error("Failed to copy response:", error);
      addNotification("error", "Unable to copy response");
    }
  };

  const formatSessionTime = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const renderSessionList = () => (
    <div className="space-y-2">
      {sessions.length === 0 ? (
        <div className="text-xs text-slate-500 px-3 py-4 text-center border border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
          No sessions yet
        </div>
      ) : (
        sessions.map((session) => {
          const isActive = session.id === model2SessionId;
          const meta = sessionMeta[session.id];
          const preview = meta?.preview || DEFAULT_ASSISTANT_MESSAGE;
          const messageCount = meta?.messageCount ?? session.message_count ?? 0;
          const updatedAt = meta?.updated || session.updated;
          return (
            <div
              key={session.id}
              className={cn(
                "w-full p-2 rounded-lg border transition-colors flex items-start gap-2",
                isActive
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                  : "border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 bg-white dark:bg-slate-900",
              )}
            >
              <button
                type="button"
                onClick={() => handleSwitchSession(session.id)}
                className="flex-1 min-w-0 text-left p-1"
              >
                <p
                  className={cn(
                    "text-xs font-semibold truncate",
                    isActive
                      ? "text-indigo-700 dark:text-indigo-300"
                      : "text-slate-700 dark:text-slate-200",
                  )}
                >
                  {preview}
                </p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500">
                    {messageCount} messages
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {formatSessionTime(updatedAt)}
                  </span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => openDeleteSessionModal(session.id)}
                disabled={isSessionActionLoading || deletingSessionId === session.id}
                className="shrink-0 p-2 rounded-md text-rose-500 hover:text-rose-700 hover:bg-rose-200 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Delete chat session"
                title="Delete chat session"
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })
      )}
    </div>
  );

  const sessionPendingDelete = pendingDeleteSessionId
    ? sessions.find((session) => session.id === pendingDeleteSessionId)
    : null;
  const pendingDeletePreview = sessionPendingDelete
    ? sessionMeta[sessionPendingDelete.id]?.preview || DEFAULT_ASSISTANT_MESSAGE
    : DEFAULT_ASSISTANT_MESSAGE;

  return (
    <>
      <AnimatePresence>
        {isHistoryOpenMobile && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50"
              onClick={() => setIsHistoryOpenMobile(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.3 }}
              className="fixed inset-y-0 left-0 z-50 w-[86vw] max-w-[360px] bg-slate-50 dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 p-4 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-900 dark:text-white">
                  Chat History
                </h2>
                <button
                  onClick={() => setIsHistoryOpenMobile(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
                {renderSessionList()}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {pendingDeleteSessionId && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black/50"
              onClick={closeDeleteSessionModal}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3 sm:p-6"
            >
              <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl p-4 sm:p-5">
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Delete chat session?
                </h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  This action cannot be undone.
                </p>
                <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3">
                  <p className="text-[11px] text-slate-500 mb-1">
                    Session preview
                  </p>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 line-clamp-2">
                    {pendingDeletePreview}
                  </p>
                </div>
                <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={closeDeleteSessionModal}
                    disabled={isSessionActionLoading}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={confirmDeleteSession}
                    disabled={isSessionActionLoading}
                    className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    {isSessionActionLoading ? "Deleting..." : "Delete Session"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="flex h-full min-h-0 max-w-7xl mx-auto w-full overflow-hidden px-2 sm:px-4 lg:px-8">
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="shrink-0 py-3 mb-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 bg-indigo-500 blur-lg opacity-20 rounded-full"></div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg relative z-10">
                    <Bot className="text-white" size={20} />
                  </div>
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight truncate">
                    DDRE Intelligence
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsHistoryOpenMobile(true)}
                  disabled={isSessionActionLoading || isInitializingChat}
                  className="px-3 py-2 text-xs"
                >
                  <History size={14} /> History
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleNewChat}
                  disabled={
                    isSessionActionLoading || isLoading || isInitializingChat
                  }
                  className="px-3 py-2 text-xs"
                >
                  <PlusCircle size={14} /> New Chat
                </Button>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1 sm:pr-2 custom-scrollbar space-y-4 sm:space-y-6 pb-4">
            {isInitializingChat ? (
              <div className="h-full flex items-center justify-center">
                <div className="flex items-center gap-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-5 py-2 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" />
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-100" />
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-200" />
                  </div>
                  <span className="text-xs font-medium text-slate-500">
                    Loading session...
                  </span>
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && !isLoading && (
                  <div className="h-full flex items-center justify-center text-center text-slate-500">
                    <div>
                      <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-full mx-auto mb-3 flex items-center justify-center">
                        <Sparkles size={18} className="text-indigo-500" />
                      </div>
                      <p className="text-sm font-medium">
                        Hey, let&apos;s get started.
                      </p>
                    </div>
                  </div>
                )}

                <AnimatePresence>
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={cn(
                        "flex gap-2 sm:gap-3",
                        message.role === "user"
                          ? "justify-end"
                          : "justify-start",
                      )}
                    >
                      {message.role === "assistant" && (
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 border border-slate-200 dark:border-slate-700">
                          <BrainCircuit
                            size={14}
                            className="text-slate-600 dark:text-slate-400"
                          />
                        </div>
                      )}

                      <div className="max-w-[92%] sm:max-w-[75%]">
                        <div
                          className={cn(
                            "rounded-2xl p-3 sm:p-4 shadow-sm text-sm",
                            message.role === "user"
                              ? "bg-indigo-600 text-white"
                              : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800",
                          )}
                        >
                          {message.role === "assistant" ? (
                            typingMessageId === message.id ? (
                              <Typewriter
                                content={message.content}
                                animate
                                sourceTitleById={sourceTitleById}
                                speed={12}
                                onComplete={() =>
                                  setTypingMessageId((current) =>
                                    current === message.id ? null : current,
                                  )
                                }
                              />
                            ) : (
                              formatMessage(message.content, sourceTitleById)
                            )
                          ) : (
                            <p className="leading-relaxed whitespace-pre-wrap">
                              {message.content}
                            </p>
                          )}
                        </div>

                        {message.role === "assistant" && (
                          <div className="mt-1 flex justify-end">
                            <button
                              onClick={() => void copyToClipboard(message.content)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                            >
                              <Copy size={11} />
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-center py-4"
                  >
                    <div className="flex items-center gap-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-5 py-2 shadow-sm">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" />
                        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-100" />
                        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-200" />
                      </div>
                      <span className="text-xs font-medium text-slate-500">
                        Thinking...
                      </span>
                    </div>
                  </motion.div>
                )}
              </>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="shrink-0 pt-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
            <form onSubmit={handleSubmit} className="relative group">
              {/* <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl opacity-20 group-hover:opacity-40 transition duration-500 blur"></div> */}
              <div className="relative w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg flex items-center">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything..."
                  className="flex-1 bg-transparent py-3.5 sm:py-4 pl-4 sm:pl-6 pr-14 sm:pr-16 focus:outline-none text-sm text-slate-800 dark:text-slate-100"
                  disabled={
                    isLoading || isSessionActionLoading || isInitializingChat
                  }
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Button
                    type="submit"
                    disabled={
                      !input.trim() ||
                      isLoading ||
                      isSessionActionLoading ||
                      isInitializingChat ||
                      !model2SessionId
                    }
                    className="p-2 h-9 w-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-md flex items-center justify-center transition-all active:scale-95"
                  >
                    <Send
                      size={16}
                      className={isLoading ? "opacity-50" : "opacity-100"}
                    />
                  </Button>
                </div>
              </div>
            </form>
            <p className="text-center text-[10px] text-slate-400 mt-3 flex items-center justify-center gap-2">
              <Info size={10} /> Responses generated by DDRE Proprietary Engine
            </p>
            </div>
          </div>
        </div>
    </>
  );
}
